/**
 * AircraftSpritesOverlay — 2D top-view PNG sprites as loading placeholders
 *
 * Shows positioned top-view images of aircraft while 3D models stream in.
 * Each sprite fades out once its corresponding 3D model has loaded.
 * Uses the same RAF position loop as aircraft labels (useMapLabelPositions).
 *
 * Toggled via rampViewConfigStore.showSpritePreviews.
 * Auto-hides when all 3D models are loaded.
 */

import { useEffect, useRef, useMemo, createRef } from 'react'
import type { PlacedAircraft3D } from '@/services/3d'
import type { LabelPositionData } from '@/hooks/useMapLabelPositions'

interface AircraftSpritesOverlayProps {
  aircraftData: PlacedAircraft3D[]
  /** Set of allocation IDs whose 3D models have loaded — those sprites fade out */
  loaded3DIds: Set<string>
  /** Subscribe to the shared RAF position loop */
  onFrame: (callback: (positions: LabelPositionData) => void) => () => void
}

/**
 * Approximate pixels-per-foot at different zoom levels.
 * At zoom 18, ~1 foot ≈ 2.5 pixels.  We compute from the Mapbox formula:
 * pxPerMeter = 2^zoom / (78271.484 * cos(lat)) but since lat varies,
 * we use a simpler heuristic: scale the sprite based on the zoom delta from 18.
 */

export function AircraftSpritesOverlay({
  aircraftData,
  loaded3DIds,
  onFrame,
}: AircraftSpritesOverlayProps) {
  // Filter to aircraft that have top-view images and haven't loaded 3D yet
  const spriteAircraft = useMemo(() => {
    return aircraftData.filter((ac) => {
      if (loaded3DIds.has(ac.id)) return false
      return !!ac.aircraftType.modelSettings.images?.top
    })
  }, [aircraftData, loaded3DIds])

  // Per-sprite DOM refs for imperative positioning
  const spriteRefsMap = useRef<Map<string, React.RefObject<HTMLDivElement | null>>>(new Map())

  // Track current zoom for sizing
useMemo(() => {
    const newMap = new Map<string, React.RefObject<HTMLDivElement | null>>()
    for (const ac of spriteAircraft) {
      newMap.set(ac.id, spriteRefsMap.current.get(ac.id) || createRef<HTMLDivElement>())
    }
    spriteRefsMap.current = newMap
  }, [spriteAircraft])

  // Subscribe to the shared RAF position loop
  useEffect(() => {
    if (spriteAircraft.length === 0) return

    const unsubscribe = onFrame((positions: LabelPositionData) => {
      for (const [id, ref] of spriteRefsMap.current) {
        const el = ref.current
        if (!el) continue

        // Use aircraftBody position (model center, not label offset)
        const pos = positions.aircraftBody.get(id) ?? positions.aircraft.get(id)
        if (!pos || !pos.visible) {
          el.style.display = 'none'
          continue
        }

        el.style.display = 'block'
        // Translate so the sprite center aligns with the aircraft position
        el.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -50%)`
      }
    })

    return unsubscribe
  }, [onFrame, spriteAircraft.length])

  // Listen for zoom changes to update sprite sizes
  useEffect(() => {
    if (spriteAircraft.length === 0) return

    // We don't have direct map access here, but we can read zoom from
    // the positions callback. For simplicity, we handle sizing via CSS
    // custom properties set by the parent. For now, use a fixed size
    // that works well at the typical ramp view zoom (17-19).
  }, [spriteAircraft.length])

  // Auto-hide when all sprites are loaded
  if (spriteAircraft.length === 0) return null

  return (
    <div className="absolute inset-0 z-[5] pointer-events-none overflow-hidden">
      {spriteAircraft.map((aircraft) => {
        const topUrl = aircraft.aircraftType.modelSettings.images?.top
        if (!topUrl) return null

        // Use physical dimensions for sizing (fallback to generic size)
        const wingspan = aircraft.physicalDimensions?.wingspan ?? 40
        const length = aircraft.physicalDimensions?.length ?? 35
        // Convert to approximate screen pixels using a base scale
        // The actual scale depends on zoom, but we use CSS transform for that
        const basePxPerFoot = 2.5 // ~zoom 18
        const widthPx = wingspan * basePxPerFoot
        const heightPx = length * basePxPerFoot

        // Heading rotation in degrees
        const heading = aircraft.rotation

        return (
          <div
            key={aircraft.id}
            ref={spriteRefsMap.current.get(aircraft.id)}
            className="absolute top-0 left-0 will-change-transform"
            style={{
              display: 'none', // Hidden until first position update
              transition: 'opacity 0.4s ease-out',
              opacity: loaded3DIds.has(aircraft.id) ? 0 : 0.85,
            }}
          >
            <img
              src={topUrl}
              alt=""
              draggable={false}
              style={{
                width: `${widthPx}px`,
                height: `${heightPx}px`,
                transform: `rotate(${heading}deg)`,
                objectFit: 'contain',
                filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))',
                pointerEvents: 'none',
              }}
            />
          </div>
        )
      })}
    </div>
  )
}
